import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Currency } from '../bank-account/bank-account';
import { ControlsOf } from '../../utilities/form.types';
import { ExpenseReceiptVendor } from './expense-receipt';

/** One cost center of a receipt line: its share and, in review, the expense account chosen instead of the rule's. */
export interface ExpenseReceiptSplitFormModel {
  id: string | null;
  costCenterReference: string | null;
  /** ISV included — this cost center's part of what the receipt says. */
  total: number | null;
  /** Accounting review only. */
  glAccountOverrideReference: string | null;
}

export type ExpenseReceiptSplitForm = FormGroup<ControlsOf<ExpenseReceiptSplitFormModel>>;

/** One line of the receipt editor (#5794): concept, description and ISV rate, split across one or more cost centers. */
export interface ExpenseReceiptLineFormModel {
  id: string | null;
  description: string | null;
  conceptCode: string | null;
  /** 0.15, or null for no ISV. */
  taxRate: number | null;
}

export type ExpenseReceiptLineForm = FormGroup<ControlsOf<ExpenseReceiptLineFormModel> & { splits: FormArray<ExpenseReceiptSplitForm> }>;

/** The receipt editor's header; lines live in their own typed FormArray. */
export interface ExpenseReceiptHeaderFormModel {
  vendor: ExpenseReceiptVendor | null;
  date: Date | null;
  fiscalNumber: string | null;
  taxAuthorizationNumber: string | null;
  currency: Currency;
  rateToUsd: number | null;
}

export type ExpenseReceiptForm = FormGroup<ControlsOf<ExpenseReceiptHeaderFormModel> & { lines: FormArray<ExpenseReceiptLineForm> }>;

export function newReceiptSplitForm(value?: Partial<ExpenseReceiptSplitFormModel>): ExpenseReceiptSplitForm {
  return new FormGroup<ControlsOf<ExpenseReceiptSplitFormModel>>({
    id: new FormControl<string | null>(value?.id ?? null),
    costCenterReference: new FormControl<string | null>(value?.costCenterReference ?? null),
    total: new FormControl<number | null>(value?.total ?? null),
    glAccountOverrideReference: new FormControl<string | null>(value?.glAccountOverrideReference ?? null),
  });
}

/** A new line starts with one empty cost center; a saved one brings its splits. */
export function newReceiptLineForm(value?: Partial<ExpenseReceiptLineFormModel>, splits?: ExpenseReceiptSplitForm[]): ExpenseReceiptLineForm {
  return new FormGroup({
    id: new FormControl<string | null>(value?.id ?? null),
    description: new FormControl<string | null>(value?.description ?? null),
    conceptCode: new FormControl<string | null>(value?.conceptCode ?? null),
    taxRate: new FormControl<number | null>(value === undefined ? DEFAULT_TAX_RATE : (value.taxRate ?? null)),
    splits: new FormArray<ExpenseReceiptSplitForm>(splits?.length ? splits : [newReceiptSplitForm()]),
  });
}

/** ISV rates the editor offers; only Honduras' general rate for now (the API refuses any other). */
export const TAX_RATE_OPTIONS: { label: string; value: number }[] = [{ label: '15%', value: 0.15 }];
export const DEFAULT_TAX_RATE = 0.15;

/** The API's split, so the editor's totals match what is saved: before ISV = total ÷ (1 + rate), to cents. */
export function splitTax(total: number, rate: number | null): { amount: number; tax: number } {
  const gross = Math.round(total * 100) / 100;
  const amount = rate && rate > 0 ? Math.round((gross / (1 + rate)) * 100) / 100 : gross;
  return { amount, tax: Math.round((gross - amount) * 100) / 100 };
}

/** Field names the API reports as missing → translation keys. */
export const receiptMissingFieldLabels: Record<string, string> = {
  Vendor: 'Receipt_Field_Vendor',
  Date: 'Receipt_Field_Date',
  Lines: 'Receipt_Field_Lines',
  'Exchange rate': 'Receipt_Field_Rate',
  Concept: 'Receipt_Field_Concept',
  'Cost center': 'Receipt_Field_CostCenter',
  Amount: 'Receipt_Field_Amount',
};
